import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { useSystemParameterStore } from '~library/stores'
import { MaintenanceEnabled, SystemParameterKey } from '~library/enums'
import { useDate } from '~library/composables'
import { api } from '@/services'
import { RouterName } from '@/enums/router-name'

type SystemParameterResult = Awaited<
  ReturnType<ReturnType<typeof useSystemParameterStore>['getSystemParameter']>
>

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function getEnabledMaintenanceWhiteListPatterns(
  whiteListRes: SystemParameterResult
): string[] {
  return whiteListRes.flatMap(({ paramList }) =>
    paramList
      .filter(({ code, enable }) => enable && Boolean(code))
      .map(({ code }) => code)
  )
}

function isIpMatchedWhiteList(userIp: string, whiteListPatterns: string[]) {
  return whiteListPatterns.some((pattern) => {
    const regexp = new RegExp(
      `^${escapeRegExp(pattern).replace(/\\\*/g, '[^.]+')}$`
    )

    return regexp.test(userIp)
  })
}

async function getUserIp(): Promise<string> {
  try {
    const response = await api.commonUnAuth.getUserIp()
    const userIpData = response.data.data

    return typeof userIpData === 'string' ? userIpData : ''
  } catch {
    return ''
  }
}

/** 判斷系統停機狀態，回傳應導向的路由（不在維護中且非停留維護頁時回傳 undefined 代表放行） */
export async function resolveMaintenanceRedirect(
  to: RouteLocationNormalized
): Promise<RouteLocationRaw | undefined> {
  const { dayjs, nowDate } = useDate()
  const { getSystemParameter } = useSystemParameterStore()

  // 停機判斷需即時，第三參數傳 false 強制不走快取
  const enabled = (
    await getSystemParameter(
      SystemParameterKey.MAINTENANCE_ENABLED,
      null,
      false
    )
  )[0]?.paramList?.[0]?.code

  // 只有開啟維護開關才需取得起訖時間與白名單，避免非維護期間多打 API
  let isMaintenance = false
  if (enabled === MaintenanceEnabled.Y) {
    const [startRes, endRes, whiteListRes] = await Promise.all([
      getSystemParameter(
        SystemParameterKey.MAINTENANCE_START_DATE,
        null,
        false
      ),
      getSystemParameter(SystemParameterKey.MAINTENANCE_END_DATE, null, false),
      getSystemParameter(SystemParameterKey.MAINTENANCE_WHITE_LIST, null, false)
    ])
    const start = startRes[0]?.paramList?.[0]?.code
    const end = endRes[0]?.paramList?.[0]?.code
    const whiteListPatterns =
      getEnabledMaintenanceWhiteListPatterns(whiteListRes)
    const userIp = await getUserIp()
    const isInMaintenanceTime = dayjs(nowDate.value).isBetween(
      dayjs(start),
      dayjs(end),
      'second',
      '[]'
    )
    // 只有 WHETHER_ENA = 1（enable = true）且後端回傳 IP 命中 CODE pattern 才放行。
    const isWhiteListMatched = isIpMatchedWhiteList(userIp, whiteListPatterns)

    isMaintenance = isInMaintenanceTime && !isWhiteListMatched
  }

  if (isMaintenance) {
    return to.name === RouterName.SYSTEM
      ? undefined
      : { name: RouterName.SYSTEM }
  }

  // 非維護期間若停留在維護頁（例如重整），導回首頁
  return to.name === RouterName.SYSTEM ? { name: RouterName.HOME } : undefined
}
